/**
 * OutfitCompletionService — Compléter une tenue avec des accessoires
 *
 * À partir d'une tenue partielle (robe seule, ou haut+bas), trouve les
 * meilleures pièces complémentaires À ACHETER depuis le catalogue :
 *   - Sacs
 *   - Chaussures
 *   - Accessoires
 *   - Bijoux
 *
 * Source : CATALOGUE UNIQUEMENT (objectif = aider l'user à acheter ce qui
 * manque pour parfaire sa tenue, pas re-utiliser ce qu'il a déjà).
 *
 * Le matching utilise AIMatchingService.matchScore avec userAge en context
 * pour privilégier les pièces adaptées à l'âge user.
 */
import { matchScore } from './AIMatchingService';
import { getCachedPrefs } from './DailyPreferencesService';

// Import lazy + fallback pour les helpers UserContextService.
// Si pour une raison ou une autre le module n'a pas exporté ces fonctions,
// on dégrade gracieusement (pas de morpho/age dans le scoring) plutôt que
// de crasher toute l'app au bundle.
function safeGetUserContext() {
  try {
    const m = require('./UserContextService');
    return {
      userAgeGroup:   typeof m.getCachedUserAgeGroup   === 'function' ? m.getCachedUserAgeGroup()   : null,
      userSilhouette: typeof m.getCachedUserSilhouette === 'function' ? m.getCachedUserSilhouette() : null,
    };
  } catch (_) {
    return { userAgeGroup: null, userSilhouette: null };
  }
}

// Catégories complémentaires à proposer pour compléter une tenue
const COMPLEMENTARY_CATEGORIES = ['Sacs', 'Chaussures', 'Accessoires', 'Bijoux'];

// Score minimum pour qu'une pièce complémentaire soit proposée
const MIN_MATCH_SCORE = 65;

/**
 * Propose les meilleures pièces complémentaires pour compléter une tenue.
 * UNIQUEMENT depuis le catalogue (articles à acheter).
 *
 * @param {Array} baseItems     Articles de base (robe ou haut+bas) déjà choisis
 * @param {Array} catalog       Articles du catalogue à proposer
 * @param {Object} options      { tempC, occasion, maxPerCategory }
 *
 * @returns {Object} {
 *   sacs:        [{ item, score }],
 *   chaussures:  [{ item, score }],
 *   accessoires: [{ item, score }],
 *   bijoux:      [{ item, score }],
 * }
 */
export function completeOutfit(baseItems, catalog = [], options = {}) {
  const {
    tempC = null,
    occasion = null,
    maxPerCategory = 6,
  } = options;

  // Contexte commun pour le scoring (safe fallback si helpers absents)
  const { userAgeGroup, userSilhouette } = safeGetUserContext();
  const dailyPrefs = getCachedPrefs();
  const context = { tempC, occasion, userAgeGroup, userSilhouette, dailyPrefs };

  if (!baseItems || baseItems.length === 0) return emptyResult();

  // Évite de re-proposer un article déjà dans baseItems
  const baseIds = new Set(baseItems.map(i => i.id || i.name));

  // Pour chaque catégorie complémentaire, scorer + trier
  const result = emptyResult();
  for (const category of COMPLEMENTARY_CATEGORIES) {
    const candidates = (catalog || [])
      .filter(c => c.category === category && !baseIds.has(c.id || c.name))
      .map(c => ({
        item: c,
        score: averageMatchScore(c, baseItems, context),
      }))
      .filter(r => r.score >= MIN_MATCH_SCORE)
      .sort((a, b) => b.score - a.score)
      .slice(0, maxPerCategory);

    const key = categoryToKey(category);
    if (key) result[key] = candidates;
  }

  return result;
}

/**
 * Score moyen entre un article candidat et tous les articles de base.
 * Le candidat doit matcher avec CHACUN, pas juste un seul.
 */
function averageMatchScore(candidate, baseItems, context) {
  if (!baseItems || baseItems.length === 0) return 0;
  const scores = baseItems.map(b => matchScore(candidate, b, context));
  // Moyenne pondérée : pénalité si un seul mismatch grave
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const minScore = Math.min(...scores);
  // Si un score est très bas, on tire la moyenne vers le bas pour éviter
  // qu'un sac matche bien une robe mais pas du tout les chaussures.
  if (minScore < 50) return Math.round(avg * 0.7);
  return Math.round(avg);
}

function categoryToKey(category) {
  switch (category) {
    case 'Sacs':        return 'sacs';
    case 'Chaussures':  return 'chaussures';
    case 'Accessoires': return 'accessoires';
    case 'Bijoux':      return 'bijoux';
    default: return null;
  }
}

function emptyResult() {
  return {
    sacs:        [],
    chaussures:  [],
    accessoires: [],
    bijoux:      [],
  };
}

/**
 * Retourne true si une tenue est "incomplète" (pourrait bénéficier de complétion).
 * Une tenue est incomplète si elle n'a pas de chaussures OU pas de sac OU pas d'accessoire.
 */
export function isOutfitIncomplete(items) {
  if (!items || items.length === 0) return true;
  const cats = new Set(items.map(i => i.category));
  // On considère qu'une tenue est "complétable" si elle a des bases mais pas
  // tous les accessoires utiles
  const hasBase = cats.has('Robes') || (cats.has('Hauts') && cats.has('Bas'));
  if (!hasBase) return false; // pas de base → ce n'est pas une "tenue à compléter"
  const missing = COMPLEMENTARY_CATEGORIES.filter(c => !cats.has(c));
  return missing.length > 0;
}

/**
 * Renvoie la liste des catégories manquantes dans une tenue.
 */
export function getMissingCategories(items) {
  if (!items || items.length === 0) return COMPLEMENTARY_CATEGORIES;
  const cats = new Set(items.map(i => i.category));
  return COMPLEMENTARY_CATEGORIES.filter(c => !cats.has(c));
}

export default {
  completeOutfit,
  isOutfitIncomplete,
  getMissingCategories,
  COMPLEMENTARY_CATEGORIES,
};
